import React, { useEffect, useRef, useState } from "react";
import InputAuto from "./InputAuto";
import { API } from "../api/api";

// Server-side search wrapper around InputAuto. Endpoint must accept `term` and
// `selectedId` query params and return { status, result: [{ id, label, subtitle? }] }.
// Debounces input at 300ms, caps at 20 rows per query.
const ServerSearchAutocomplete = ({
  value,
  onChange,
  endpoint,
  placeholder,
  label,
  name,
  size = "small",
  variant = "outlined",
  disableClearable = false,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  // Remember the user's pick so it stays renderable even after options get
  // swapped by a subsequent search fetch.
  const [selectedOption, setSelectedOption] = useState(null);
  const debounceRef = useRef(null);

  const fetchOptions = (term, selId) => {
    setLoading(true);
    // Trailing `&_=` absorbs the slash that API() appends, so query params
    // don't get corrupted (e.g. `selectedId=39/` would fail int binding).
    const qs = term
      ? `?term=${encodeURIComponent(term)}&_=`
      : selId
      ? `?selectedId=${selId}&_=`
      : "?_=";
    API(endpoint + qs).get((res) => {
      setLoading(false);
      if (res?.status === 200) setOptions(res.result || []);
    });
  };

  // Initial fetch — pass current value as selectedId so the label renders even
  // if the preselected id isn't in the default top-N set.
  useEffect(() => {
    fetchOptions("", value);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the parent clears the value, drop the sticky selection too.
  useEffect(() => {
    if (!value) setSelectedOption(null);
  }, [value]);

  const handleInput = (_e, term, reason) => {
    if (reason !== "input") return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchOptions(term || ""), 300);
  };

  // Merge the sticky selected option into the visible options so InputAuto's
  // `options.find(id === value)` always succeeds.
  const mergedOptions =
    selectedOption && !options.some((o) => o.id === selectedOption.id)
      ? [selectedOption, ...options]
      : options;

  return (
    <InputAuto
      name={name}
      value={value}
      options={mergedOptions}
      loading={loading}
      noFilter
      disableClearable={disableClearable}
      placeholder={placeholder}
      label={label}
      size={size}
      variant={variant}
      onChange={(e, picked) => {
        setSelectedOption(picked || null);
        // Forward the full picked option so callers can read extras
        // (existing call sites that take only the id ignore the 2nd arg).
        onChange(e?.target?.value ?? "", picked || null);
      }}
      onInput={handleInput}
    />
  );
};

export default ServerSearchAutocomplete;
